import { needsSocialRegistration } from '~/utils/social-registration';
import { syncCustomerPricing } from '~/utils/customer-login-sync';
// app/components/magic-link-handler.tsx
import { useEffect } from 'react';
import { useNavigate, useLocation } from '@remix-run/react';
import { klaviyo, trackLogin } from '~/utils/analytics';

export default function MagicLinkHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we have an access token in the URL hash
    if (location.hash) {
      try {
        // Parse URL hash parameters
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const accessToken = hashParams.get('access_token');

        if (accessToken) {
          console.log('Found access token in URL hash');

          // Call the auth endpoint to verify and get user data
          fetch('https://budflow.com/catalyst/auth/user/', {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error('Failed to authenticate with the token');
              }
              return response.json();
            })
            .then(async (data) => {
              const needsRegistration = needsSocialRegistration(data);
              if (!needsRegistration && location.pathname !== '/register')
                await syncCustomerPricing(accessToken);

              // Do not retain another customer's profile when switching social accounts.
              for (const key of [
                'alpine_iq_id',
                'email',
                'first_name',
                'last_name',
                'dob',
                'phone',
              ]) {
                localStorage.removeItem(key);
                document.cookie = `${key}=; path=/; max-age=0`;
              }

              // Store user ID
              if (data.id) {
                const oneYearInSeconds = 60 * 60 * 24 * 365;
                localStorage.setItem('catalystUserId', data.id);
                document.cookie = `catalystUserId=${data.id}; path=/; max-age=${oneYearInSeconds}`;
              }

              // Store app_metadata fields
              if (data.app_metadata) {
                const { alpine_iq_id, email, first_name, last_name, dob, phone } =
                  data.app_metadata;
                const oneYearInSeconds = 60 * 60 * 24 * 365;

                if (alpine_iq_id) {
                  localStorage.setItem('alpine_iq_id', alpine_iq_id);
                  document.cookie = `alpine_iq_id=${encodeURIComponent(alpine_iq_id)}; path=/; max-age=${oneYearInSeconds}`;
                }

                if (email) {
                  localStorage.setItem('email', email);
                  document.cookie = `email=${encodeURIComponent(email)}; path=/; max-age=${oneYearInSeconds}`;
                }

                if (first_name) {
                  localStorage.setItem('first_name', first_name);
                  document.cookie = `first_name=${encodeURIComponent(first_name)}; path=/; max-age=${oneYearInSeconds}`;
                }

                if (last_name) {
                  localStorage.setItem('last_name', last_name);
                  document.cookie = `last_name=${encodeURIComponent(last_name)}; path=/; max-age=${oneYearInSeconds}`;
                }

                if (dob) {
                  localStorage.setItem('dob', dob);
                  document.cookie = `dob=${encodeURIComponent(dob)}; path=/; max-age=${oneYearInSeconds}`;
                }

                if (phone) {
                  localStorage.setItem('phone', phone);
                  document.cookie = `phone=${encodeURIComponent(phone)}; path=/; max-age=${oneYearInSeconds}`;
                }
              }

              // 🔥 Klaviyo Enhanced Analytics - User identification and login tracking
              if (data.app_metadata) {
                const { email, first_name, last_name, dob, phone } = data.app_metadata;
                klaviyo.identify({
                  email: email,
                  phone: phone,
                  first_name: first_name,
                  last_name: last_name,
                  dob: dob,
                });
                klaviyo.login('Magic Link');

                // 🔥 GA4 Enhanced Ecommerce - Login event
                trackLogin('Magic Link');
              }

              // Store the access token (localStorage + cookie). Phase 6: the
              // COOKIE is what SSR loaders read for personalised product_search.
              const yr = 60 * 60 * 24 * 365;
              const expiresAt = hashParams.get('expires_at');
              const expiresInNum = expiresAt
                ? Math.max(60, parseInt(expiresAt, 10) - Math.floor(Date.now() / 1000))
                : 3600;
              localStorage.setItem('access_token', accessToken);
              document.cookie = `access_token=${accessToken}; path=/; max-age=${expiresInNum}; SameSite=Lax`;

              // Get token expiration if present and store it
              if (expiresAt) {
                localStorage.setItem('token_expires_at', expiresAt);
                document.cookie = `token_expires_at=${expiresAt}; path=/; max-age=${yr}; SameSite=Lax`;
              }

              // Get refresh token if present and store it
              const refreshToken = hashParams.get('refresh_token');
              if (refreshToken) {
                localStorage.setItem('refresh_token', refreshToken);
                document.cookie = `refresh_token=${refreshToken}; path=/; max-age=${yr}; SameSite=Lax`;
              }

              // Dispatch the login event
              if (!needsRegistration || location.pathname === '/register')
                window.dispatchEvent(new Event('user:login'));

              // Clean up the URL by removing the hash
              navigate(needsRegistration || location.pathname === '/register' ? '/register' : '/', {
                replace: true,
              });
            })
            .catch((error) => {
              console.error('Authentication error:', error);
              // Optionally navigate to login page or show error
              navigate('/link-expired/', { replace: true });
            });
        }
      } catch (error) {
        console.error('Error processing magic link:', error);
      }
    }
  }, [location.hash, navigate]);

  // This component doesn't render anything
  return null;
}
